import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { storePayoutsApi } from '../services/api';
import { errorText, gatewayWindowWarning, validatePayoutSettings } from './storePayoutsUtils';

const toDraft = (s) => ({
  returnWindowDays: String(s.returnWindowDays),
  bufferDays: String(s.bufferDays),
  refundAdminFeePercent: String(s.refundAdminFeePercent),
});

/** Return window, payout buffer and refund admin fee. Client validation mirrors the server. */
const PayoutSettingsCard = () => {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await storePayoutsApi.getSettings();
      setSaved(data);
      setDraft(toDraft(data));
    } catch (err) {
      setSaved(null);
      setDraft(null);
      setLoadError(errorText(err, 'Failed to load payout settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validity = useMemo(() => (draft ? validatePayoutSettings(draft) : null), [draft]);
  const warning = validity?.valid ? gatewayWindowWarning(validity.body, saved?.gatewayMaxHoldDays) : '';

  const change = (key) => (e) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
    setSuccess('');
    setSaveError('');
  };

  const handleSave = async () => {
    if (!validity?.valid) return;
    setSaving(true);
    setSaveError('');
    setSuccess('');
    try {
      const result = await storePayoutsApi.updateSettings(validity.body);
      setSaved(result);
      setDraft(toDraft(result));
      setSuccess('Settings saved. They apply to payouts scheduled from now on and to the next refund.');
    } catch (err) {
      const details = Array.isArray(err?.details) ? err.details.filter(Boolean) : [];
      const base = errorText(err, 'The settings could not be saved.');
      setSaveError(details.length ? `${base}: ${details.join('; ')}` : base);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, helper) => (
    <TextField
      size="small"
      label={label}
      value={draft[key]}
      onChange={change(key)}
      disabled={saving}
      error={!!validity.errors[key]}
      helperText={validity.errors[key] || helper}
      inputProps={{ inputMode: 'decimal' }}
      sx={{ minWidth: 220, flex: 1 }}
    />
  );

  return (
    <Paper sx={{ p: 2, mb: 3 }} aria-label="Payout settings">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Payout settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A store is paid out the return window plus the buffer after the order is delivered. A payout
        that already has a due date keeps it.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Loading payout settings" />
        </Box>
      )}

      {!loading && loadError && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {!loading && draft && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {field('returnWindowDays', 'Return window (days)', 'Whole days, 0 to 365.')}
            {field('bufferDays', 'Buffer (days)', 'Extra days after the window, 0 to 90.')}
            {field(
              'refundAdminFeePercent',
              'Refund admin fee (%)',
              'Share of the commission AniPaw keeps on a refund, 0 to 100. 0 returns all of it.',
            )}
          </Box>
          {saved?.gatewayMaxHoldDays != null && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              PayPal pays held money out by itself {saved.gatewayMaxHoldDays} days after capture.
            </Typography>
          )}
          {warning && <Alert severity="warning" sx={{ mt: 2 }}>{warning}</Alert>}
          {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!validity.valid || saving}
            >
              Save settings
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default PayoutSettingsCard;
